import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { detectMediaType } from "../utils/MediaUtils";
import AttachmentSheet from "./AttachmentSheet";
import MessageMedia from "./MessageMedia";

/* ─── SVG Icons ─────────────────────────────────────────────────────────── */
const EmojiIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
  </svg>
);

const KeyboardIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <path d="M20 5H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 5H5v-2h2v2zm10 0H7v-2h10v2zm0-3h-2v-2h2v2zm0-3h-2V8h2v2zm3 6h-2v-2h2v2zm0-3h-2v-2h2v2zm0-3h-2V8h2v2z"/>
  </svg>
);

const AttachIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
  </svg>
);

const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14l-2.13-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" style={{ opacity: 0.85 }}>
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
  </svg>
);

/* ─── Animated waveform bars ────────────────────────────────────────────── */
function WaveformBars({ seconds, paused }) {
  return (
    <div className="flex items-center gap-[3px] flex-1 overflow-hidden">
      <span className="text-sm font-medium tabular-nums text-red-500 mr-2 shrink-0">
        {String(Math.floor(seconds / 60)).padStart(2, "0")}:
        {String(seconds % 60).padStart(2, "0")}
      </span>
      {Array.from({ length: 26 }).map((_, i) => (
        <div
          key={i}
          className="w-[3px] rounded-full shrink-0"
          style={{
            height: `${10 + Math.sin(i * 1.3) * 7}px`,
            backgroundColor: paused ? "#94a3b8" : "#22c55e",
            animation: paused ? "none" : `waveBar 0.6s ease-in-out infinite alternate`,
            animationDelay: `${(i * 40) % 300}ms`,
            minHeight: "4px",
            maxHeight: "22px",
          }}
        />
      ))}
    </div>
  );
}

/* ─── Mini audio preview player shown while paused ─────────────────────── */
function PausedPreviewPlayer({ url }) {
  const audioRef    = useRef(null);
  const rafRef      = useRef(null);
  const [playing,   setPlaying]   = useState(false);
  const [duration,  setDuration]  = useState(0);
  const [current,   setCurrent]   = useState(0);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [url]);

  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
    audioRef.current?.pause();
  }, []);

  const tick = useCallback(() => {
    if (!audioRef.current) return;
    setCurrent(audioRef.current.currentTime);
    if (!audioRef.current.paused) rafRef.current = requestAnimationFrame(tick);
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => { setPlaying(true); rafRef.current = requestAnimationFrame(tick); });
    } else {
      a.pause();
      setPlaying(false);
      cancelAnimationFrame(rafRef.current);
    }
  };

  const progress  = duration > 0 ? current / duration : 0;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  const scrub = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(e.clientX - rect.left, rect.width)) / rect.width;
    a.currentTime = pct * duration;
    setCurrent(a.currentTime);
  };

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={() => setDuration(isFinite(audioRef.current?.duration) ? audioRef.current.duration : 0)}
        onEnded={() => { setPlaying(false); setCurrent(0); if (audioRef.current) audioRef.current.currentTime = 0; cancelAnimationFrame(rafRef.current); }}
        onPause={() => setPlaying(false)}
      />

      <button
        type="button"
        onClick={toggle}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 hover:bg-green-600 text-white shrink-0 active:scale-90 transition-all"
        aria-label={playing ? "Pause preview" : "Play preview"}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>

      <div
        className="flex-1 flex items-center gap-[2px] h-7 cursor-pointer select-none"
        onClick={scrub}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={current}
      >
        {Array.from({ length: 30 }).map((_, i) => {
          const h = 0.15 + (((i * 7 + 13) % 17) / 17) * 0.85;
          const active = (i / 30) <= progress;
          return (
            <div
              key={i}
              className="flex-1 rounded-full transition-colors duration-75"
              style={{
                height: Math.max(3, h * 24),
                backgroundColor: active ? "#128C7E" : "rgba(0,0,0,0.18)",
                minWidth: 2,
              }}
            />
          );
        })}
      </div>

      <span className="text-[11px] font-medium tabular-nums text-neutral-500 dark:text-neutral-400 shrink-0">
        {playing || current > 0 ? fmt(current) : fmt(duration)}
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   @MENTION PICKER
   Appears above the input when user types @ in a group chat.
══════════════════════════════════════════════════════════════════════════ */
function MentionPicker({ members, query, onSelect, activeIndex }) {
  const filtered = members.filter((m) => {
    if (!query) return true;
    const name = (m.userName || m.username || m.displayName || "").toLowerCase();
    return name.includes(query.toLowerCase());
  });

  if (!filtered.length) return null;

  return (
    <div
      className="
        absolute left-0 right-0 bottom-full mb-1 z-50
        bg-white dark:bg-neutral-900
        border border-neutral-200 dark:border-neutral-700
        rounded-xl shadow-xl overflow-hidden
        max-h-[220px] overflow-y-auto
      "
      style={{ boxShadow: "0 -4px 24px rgba(0,0,0,0.10)" }}
    >
      {/* Header */}
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest select-none">
        Mention a member
      </div>

      {filtered.map((m, i) => {
        const name    = m.userName || m.username || m.displayName || String(m.chatId);
        const isActive = i === activeIndex;
        const initials = name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
        const colours  = ["#e57373","#f06292","#ba68c8","#7986cb","#64b5f6","#4db6ac","#81c784","#ffb74d","#ff8a65"];
        const colour   = colours[(name.charCodeAt(0) || 0) % colours.length];

        return (
          <button
            key={m.chatId}
            type="button"
            onMouseDown={(e) => {
              // mouseDown so focus doesn't leave the input
              e.preventDefault();
              onSelect(m);
            }}
            className={[
              "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors touch-manipulation",
              isActive
                ? "bg-green-50 dark:bg-green-900/25"
                : "hover:bg-neutral-50 dark:hover:bg-neutral-800/60",
            ].join(" ")}
          >
            {/* Avatar */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden text-white text-xs font-bold"
              style={{ backgroundColor: colour }}
            >
              {m.profileImageUrl ? (
                <img src={m.profileImageUrl} alt={name} className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>

            {/* Name + role badge */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                {name}
              </div>
              {(m.isAdmin || m.isSuperAdmin) && (
                <div className="text-[10px] text-neutral-400 dark:text-neutral-500 leading-tight">
                  {m.isSuperAdmin ? "Super Admin" : "Admin"}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ChatInput
══════════════════════════════════════════════════════════════════════════ */
export default function ChatInput({
  mobileView = false,
  input,
  pendingMedia,
  setPendingMedia,
  selectedReply,
  setSelectedReply,

  // voice
  isRecording,
  isPaused,
  startRecording,
  stopRecording,
  cancelRecording,
  pauseRecording,
  resumeRecording,
  recordingSeconds,
  previewUrl,

  // emoji
  showEmoji,
  setShowEmoji,

  // refs + handlers
  fileInputRef,
  handleInput,
  sendMessage,

  // ── Edit mode ──────────────────────────────────────────────────────────
  editTarget,
  onCancelEdit,
  onConfirmEdit,

  // ── Schedule later ──────────────────────────────────────────────────────
  onScheduleSend,

  // ── @mention: group members (only populated in group chats) ────────────
  groupMembers = [],

  // ── setInput: direct setter so mention insertion can update value ──────
  setInput,

  // ── myChatId: exclude the typing user from the mention list ────────────
  myChatId,

  disabled = false,
  disabledMessage = "You cannot send messages to this group",

  children,
}) {
  const containerRef       = useRef(null);
  const touchHandledRef    = useRef(false);
  const textareaRef        = useRef(null);
  const MAX_INPUT_HEIGHT   = 140; // about 6 lines, then it scrolls inside

  // Grow/shrink the field with its content. Re-runs after voice mode ends,
  // because the textarea is re-mounted then.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, MAX_INPUT_HEIGHT) + "px";
    el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT ? "auto" : "hidden";
  }, [input, isRecording, isPaused]);

  /* ── Attachment sheet (Document / Camera / Gallery / Video / Audio) ──── */
  const [showAttachSheet, setShowAttachSheet] = useState(false);
  const cameraInputRef      = useRef(null);
  const galleryInputRef     = useRef(null);
  const videoCameraInputRef = useRef(null);
  const videoInputRef       = useRef(null);
  const audioInputRef       = useRef(null);

  /* ── @mention state ────────────────────────────────────────────────────── */
  const [mentionQuery,       setMentionQuery]       = useState(null);   // null = closed, "" = open+empty
  const [mentionTriggerIdx,  setMentionTriggerIdx]  = useState(-1);     // caret pos of the @ sign
  const [mentionActiveIdx,   setMentionActiveIdx]   = useState(0);
  /* ── Keyboard positioning is handled by ChatSheet ─────────────────── */

  /* ── Filtered mention list (derived, not state) ─────────────────────── */
  const mentionMembers = mentionQuery !== null
    ? groupMembers.filter((m) => {
        // Never show the typing user in the picker
        if (myChatId && String(m.chatId) === String(myChatId)) return false;
        if (!mentionQuery) return true;
        const name = (m.userName || m.username || m.displayName || "").toLowerCase();
        return name.includes(mentionQuery.toLowerCase());
      })
    : [];

  const showMentionPicker =
    mentionQuery !== null &&
    groupMembers.length > 0 &&
    mentionMembers.length > 0;

      /* ── inject keyframes once ── */
  useEffect(() => {
    const id = "wa-chatinput-style";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = `
        @keyframes waveBar {
          from { transform: scaleY(0.35); }
          to   { transform: scaleY(1);   }
        }
        @keyframes micPulse {
          0%,100% { box-shadow: 0 0 0 0   rgba(34,197,94,.5); }
          50%      { box-shadow: 0 0 0 8px rgba(34,197,94,0);  }
        }
      `;
      document.head.appendChild(s);
    }
  }, []);
 
  /* ── Keyboard positioning is owned by ChatSheet ─────────────────────
     ChatInput stays as a normal child of the sheet. ChatSheet follows
     the available mobile viewport, so the composer moves with the
     keyboard instead of calculating its own keyboard offset here.
  ───────────────────────────────────────────────────────────────────── */

/* ── Publish the input's real rendered height ──────────────────────────
     ChatSheet owns the keyboard/viewport positioning. This variable is
     still useful to the message area for knowing how much composer
     space is occupied.

     MobileChatPanel keeps multiple ChatWindows mounted, so hidden
     ChatInputs can report offsetHeight === 0. Ignore those instances
     so they cannot overwrite the active composer's height.
  ───────────────────────────────────────────────────────────────────── */
useEffect(() => {
  if (!mobileView) {
    document.documentElement.style.setProperty("--chat-input-h", "0px");
    return;
  }

  const el = containerRef.current;
  if (!el || typeof ResizeObserver === "undefined") return;

  const publishHeight = () => {
    if (!el.offsetHeight) return;

    document.documentElement.style.setProperty(
      "--chat-input-h",
      `${el.offsetHeight}px`
    );
  };

  publishHeight();

  const ro = new ResizeObserver(publishHeight);
  ro.observe(el);

  return () => ro.disconnect();
}, [mobileView]);

    /* ── file-select handler ── */
  const MAX_ATTACHMENTS = 50;

  const handleFileSelect = (e) => {
    if (disabled) return;
    let files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (files.length > MAX_ATTACHMENTS) {
      files = files.slice(0, MAX_ATTACHMENTS);
    }

    const mediaItems = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      type: detectMediaType(file),
      mimeType: file.type || "",
      fileName: file.name,
      fileSize: file.size,
    }));

    setPendingMedia(mediaItems);
    e.target.value = "";
  };
  const canSend  = !disabled && (input.trim() || pendingMedia || editTarget);
  const inVoice  = isRecording || isPaused;

  /* ── @mention: parse input on every keystroke ────────────────────────── */
  const handleInputWithMention = useCallback((e) => {
    // Call the original handleInput from useTyping (handles typing indicators etc.)
    handleInput(e);

    // Only parse mentions in group chats
    if (!groupMembers.length) return;

    const val    = e.target.value;
    const caret  = e.target.selectionStart ?? val.length;

    // Walk backwards from caret to find an @ sign that starts a word
    let atIdx = -1;
    for (let i = caret - 1; i >= 0; i--) {
      const ch = val[i];
      if (ch === "@") { atIdx = i; break; }
      // Stop if we hit whitespace before finding @
      if (/\s/.test(ch)) break;
    }

    if (atIdx === -1) {
      // No active @ trigger — close picker
      setMentionQuery(null);
      setMentionTriggerIdx(-1);
      setMentionActiveIdx(0);
      return;
    }

    // The char before @ must be start-of-string or whitespace
    if (atIdx > 0 && !/\s/.test(val[atIdx - 1])) {
      setMentionQuery(null);
      setMentionTriggerIdx(-1);
      setMentionActiveIdx(0);
      return;
    }

    const query = val.slice(atIdx + 1, caret);
    setMentionQuery(query);
    setMentionTriggerIdx(atIdx);
    setMentionActiveIdx(0);
  }, [handleInput, groupMembers]);

  /* ── @mention: insert selected member ───────────────────────────────── */
  const insertMention = useCallback((member) => {
    const name = member.userName || member.username || member.displayName || String(member.chatId);
    const mention = `@${name.replace(/\s+/g, "")} `;

    // Replace from the @ sign up to the current caret
    const inputEl = containerRef.current?.querySelector(".chat-text-input");
    const caret   = inputEl?.selectionStart ?? input.length;
    const before  = input.slice(0, mentionTriggerIdx);
    const after   = input.slice(caret);
    const newVal  = before + mention + after;

    setInput?.(newVal);

    // Restore caret after the inserted mention
    const newCaret = before.length + mention.length;
    requestAnimationFrame(() => {
      if (inputEl) {
        inputEl.focus();
        inputEl.setSelectionRange(newCaret, newCaret);
      }
    });

    setMentionQuery(null);
    setMentionTriggerIdx(-1);
    setMentionActiveIdx(0);
  }, [input, mentionTriggerIdx, setInput]);

  /* ── @mention: keyboard navigation inside picker ─────────────────────── */
  const handleKeyDown = useCallback((e) => {
    if (!showMentionPicker) {
      // Normal edit-mode escape
      if (editTarget && e.key === "Escape") { onCancelEdit?.(); }
      if (!disabled && e.key === "Enter" && !e.shiftKey && !mobileView) { e.preventDefault(); handleSend(); }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionActiveIdx((i) => Math.min(i + 1, mentionMembers.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const chosen = mentionMembers[mentionActiveIdx];
      if (chosen) insertMention(chosen);
    } else if (e.key === "Escape") {
      setMentionQuery(null);
      setMentionTriggerIdx(-1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMentionPicker, mentionMembers, mentionActiveIdx, insertMention, editTarget, onCancelEdit, disabled, mobileView]);

   /* ── handle send ── */
  const handleSend = async () => {
    if (disabled) return;
    if (editTarget) { onConfirmEdit?.(); return; }

    const mediaItems = Array.isArray(pendingMedia)
      ? pendingMedia
      : pendingMedia
      ? [pendingMedia]
      : [];

    if (mediaItems.length > 0) {
      for (const item of mediaItems) {
        if (item?.file) await sendMessage(item.file, item);
      }
    } else {
      sendMessage();
    }
  };

  const canSchedule = canSend && typeof onScheduleSend === "function" && !editTarget;

 
   const containerStyle = {
    position: "relative",
    paddingTop: "8px",
    paddingBottom: "8px",
  };
   return (
  <div
    ref={containerRef}
    className="
      relative flex-shrink-0 flex flex-col gap-0 overflow-visible
      border-0
      w-full
    "
    style={{
      ...containerStyle,
      // Paint the same wallpaper ChatWindow uses behind the input.
      backgroundImage: "url(/commdoodle.jpg)",
      backgroundRepeat: "repeat",
      backgroundSize: "320px 320px",
      backgroundPosition: "center",
    }}
    onClick={(e) => e.stopPropagation()}
  >

      {/* ═══════════ EDIT INDICATOR ═══════════ */}
      {editTarget && (
        <div className="
          absolute left-0 right-0 -top-14
          mx-2 px-3 py-2
          flex items-center justify-between gap-3
          rounded-lg
          bg-neutral-100 dark:bg-neutral-800
          border-l-4 border-blue-500
          shadow-md z-10
        ">
          <div className="flex-1 overflow-hidden">
            <div className="text-xs font-semibold text-blue-500">Editing message</div>
            <div className="text-sm truncate text-neutral-700 dark:text-neutral-200">
              {editTarget.currentText}
            </div>
          </div>
          <button
            className="text-neutral-400 hover:text-red-500 p-1 touch-manipulation"
            onClick={onCancelEdit}
            aria-label="Cancel edit"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {/* ═══════════ REPLY PREVIEW ═══════════ */}
      {selectedReply && !editTarget && (
        <div className="
          absolute left-0 right-0 -top-14
          mx-2 px-3 py-2
          flex items-center justify-between gap-3
          rounded-lg
          bg-neutral-100 dark:bg-neutral-800
          border-l-4 border-green-500
          shadow-md z-10
        ">
                  <div className="flex-1 overflow-hidden">
            <div className="text-xs font-semibold text-green-600">Replying to</div>
            <div className="text-sm truncate text-neutral-700 dark:text-neutral-200">
              {selectedReply.text || (
                <MessageMedia variant="replyLabel" media={selectedReply.media} />
              )}
            </div>
          </div>
          <MessageMedia
            variant="replyThumb"
            media={selectedReply.media}
            size={40}
            className="rounded-md"
          />
          <button
            className="text-neutral-400 hover:text-red-500 p-1 touch-manipulation"
            onClick={() => setSelectedReply(null)}
            aria-label="Cancel reply"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {/* ═══════════ HIDDEN FILE INPUT — Document ═══════════
          IMPORTANT: do NOT include image/*, video/*, or audio/* wildcards
          here. Android/Samsung's file-chooser heuristics key off those
          exact wildcard mime strings — if any of them are present, the OS
          treats the input as a "media" request and surfaces the Camera /
          Camera Video / Photos-and-videos quick-action sheet instead of a
          document/file browser. Listing explicit document mime types +
          extensions (no wildcards) keeps this input in "any file" mode,
          so the OS opens the normal file browser (My Files/Drive/etc). */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
        accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.oasis.opendocument.text,application/vnd.oasis.opendocument.spreadsheet,application/vnd.oasis.opendocument.presentation,application/rtf,application/zip,application/x-zip-compressed,application/json,text/plain,text/csv,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.zip,.txt,.csv,.json"
      />

      {/* ═══════════ HIDDEN FILE INPUTS — Attachment sheet options ═══════════ */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
      />
      <input
        ref={galleryInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
      />
      <input
        ref={videoCameraInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
      />
      <input
        ref={videoInputRef}
        type="file"
        multiple
        accept="video/*"
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
      />
      {/* Same reasoning as the Document input above, but pushed further:
          it turns out the OS heuristic isn't limited to the "audio/*"
          wildcard — ANY mime string with an "audio/" prefix (even a
          specific one like "audio/mpeg") is enough to make Android/
          Samsung classify this input as a media-capture request and pop
          the Camera Video / Voice Recorder / Photos-and-videos sheet.
          The only reliable way to keep this a plain file input is to
          drop every "audio/…" mime type and accept by file extension
          only — extensions don't carry that "this is audio" signal to
          the OS chooser. */}
      <input
        ref={audioInputRef}
        type="file"
        multiple
        accept=".mp3,.m4a,.aac,.wav,.ogg,.oga,.flac,.amr,.3gp,.3gpp,.opus,.wma,.mid,.midi,.caf"
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
      />

      {/* ═══════════ ATTACHMENT SHEET ═══════════ */}
      <AttachmentSheet
        open={showAttachSheet}
        onClose={() => setShowAttachSheet(false)}
        fileInputRef={fileInputRef}
        cameraInputRef={cameraInputRef}
        galleryInputRef={galleryInputRef}
        videoCameraInputRef={videoCameraInputRef}
        videoInputRef={videoInputRef}
        audioInputRef={audioInputRef}
      />
      {/* ═══════════════════════════════════════════════════════════════
          VOICE RECORDING ROW — active (live) recording
      ═══════════════════════════════════════════════════════════════ */}
      {isRecording && !disabled && (
        <div className="flex items-center gap-2 px-2">
          <button
            type="button"
            onClick={cancelRecording}
            className="
              flex items-center justify-center w-10 h-10 shrink-0 rounded-full
              text-neutral-500 dark:text-neutral-400
              hover:bg-neutral-100 dark:hover:bg-neutral-800
              active:scale-90 transition-all touch-manipulation
            "
            aria-label="Cancel recording"
          >
            <DeleteIcon />
          </button>

          <div className="flex-1 flex items-center px-3 py-2 rounded-full min-w-0 bg-neutral-100 dark:bg-neutral-800">
            <WaveformBars seconds={recordingSeconds ?? 0} paused={false} />
          </div>

          <button
            type="button"
            onClick={pauseRecording}
            className="
              flex items-center justify-center w-9 h-9 rounded-full shrink-0
              bg-neutral-200 dark:bg-neutral-700
              text-neutral-700 dark:text-neutral-200
              hover:bg-neutral-300 dark:hover:bg-neutral-600
              active:scale-90 transition-all touch-manipulation
            "
            aria-label="Pause recording"
            title="Pause"
          >
            <PauseIcon />
          </button>

          <button
            type="button"
            onClick={stopRecording}
            className="
              flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full shrink-0
              bg-green-500 hover:bg-green-600 active:scale-90
              text-white shadow-md transition-all touch-manipulation
            "
            style={{ animation: "micPulse 1.5s ease-in-out infinite" }}
            aria-label="Send voice message"
            title="Send"
          >
            <SendIcon />
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          VOICE PAUSED ROW
      ═══════════════════════════════════════════════════════════════ */}
      {isPaused && !isRecording && !disabled && (
        <div className="flex items-center gap-2 px-2">
          <button
            type="button"
            onClick={cancelRecording}
            className="
              flex items-center justify-center w-10 h-10 shrink-0 rounded-full
              text-neutral-500 dark:text-neutral-400
              hover:bg-neutral-100 dark:hover:bg-neutral-800
              active:scale-90 transition-all touch-manipulation
            "
            aria-label="Cancel recording"
          >
            <DeleteIcon />
          </button>

          <div className="flex-1 flex items-center px-3 py-2 rounded-full min-w-0 bg-neutral-100 dark:bg-neutral-800">
            {previewUrl ? (
              <PausedPreviewPlayer url={previewUrl} />
            ) : (
              <WaveformBars seconds={recordingSeconds ?? 0} paused />
            )}
          </div>

          <button
            type="button"
            onClick={resumeRecording}
            className="
              flex items-center justify-center w-9 h-9 rounded-full shrink-0
              bg-neutral-200 dark:bg-neutral-700
              text-neutral-700 dark:text-neutral-200
              hover:bg-neutral-300 dark:hover:bg-neutral-600
              active:scale-90 transition-all touch-manipulation
            "
            aria-label="Resume recording"
            title="Resume"
          >
            <MicIcon />
          </button>

          <button
            type="button"
            onClick={stopRecording}
            className="
              flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full shrink-0
              bg-green-500 hover:bg-green-600 active:scale-90
              text-white shadow-md transition-all touch-manipulation
            "
            aria-label="Send voice message"
            title="Send"
          >
            <SendIcon />
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          NORMAL INPUT ROW
      ═══════════════════════════════════════════════════════════════ */}
      {!inVoice && (
        <div className="flex items-end gap-2 px-2 relative">

          {/* ── @MENTION PICKER — floats above input row ── */}
          {showMentionPicker && (
            <MentionPicker
              members={mentionMembers}
              query={mentionQuery}
              onSelect={insertMention}
              activeIndex={mentionActiveIdx}
            />
          )}

          <div
            className="
              flex flex-1 items-end min-w-0
              px-1 py-1
              rounded-3xl
              bg-white dark:bg-neutral-800
              shadow-sm
            "
          >
            {/* Emoji button */}
            <button
              type="button"
              disabled={disabled}
              className={`
                flex items-center justify-center w-9 h-9 mb-0.5 shrink-0 rounded-full
                transition-colors touch-manipulation
                ${disabled
                  ? "opacity-40 cursor-not-allowed text-neutral-400"
                  : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 active:scale-90"}
              `}
              onClick={(e) => {
                if (disabled) return;
                e.stopPropagation();
                setShowEmoji((prev) => {
                  const next = !prev;
                  if (!next) { const el = document.querySelector(".chat-text-input"); el?.focus(); }
                  return next;
                });
              }}
              aria-label="Emoji picker"
            >
              {showEmoji ? <KeyboardIcon /> : <EmojiIcon />}
            </button>

            {/* Text input */}
            <textarea
              ref={textareaRef}
              rows={1}
              disabled={disabled}
              className={`
                chat-text-input flex-1 px-3 sm:px-4 py-2 outline-none resize-none
                text-base sm:text-sm leading-6
                bg-transparent
                text-neutral-900 dark:text-neutral-100
                placeholder-neutral-500 dark:placeholder-neutral-400
                min-w-0
                ${disabled ? "opacity-50 cursor-not-allowed" : ""}
              `}
              style={{ maxHeight: MAX_INPUT_HEIGHT }}
              placeholder={
                disabled      ? disabledMessage  :
                editTarget    ? "Edit message…"  :
                pendingMedia  ? "Add a caption…" :
                                "Type a message…"
              }
              value={input}
              onChange={handleInputWithMention}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (showEmoji) setShowEmoji(false); }}
            />

            {/* Attach button — hidden while editing */}
            {!editTarget && (
              <button
                type="button"
                disabled={disabled}
                className={`
                  flex items-center justify-center w-9 h-9 mb-0.5 shrink-0 rounded-full
                  transition-colors touch-manipulation
                  ${disabled
                    ? "opacity-40 cursor-not-allowed text-neutral-400"
                    : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 active:scale-90"}
                `}
                onClick={() => { if (!disabled) setShowAttachSheet(true); }}
                aria-label="Attach media"
              >
                <AttachIcon />
              </button>
            )}
          </div>

          {/* Schedule button — separate, explicit tap target instead of a long-press */}
          {canSchedule && (
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                if (disabled) return;
                onScheduleSend();
              }}
              className={`
                flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full shrink-0
                transition-all touch-manipulation active:scale-90
                ${disabled
                  ? "opacity-40 cursor-not-allowed text-neutral-400"
                  : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"}
              `}
              aria-label="Schedule message"
              title="Schedule message"
            >
              <ClockIcon />
            </button>
          )}

          {/* Send / Mic button */}
          <button
            type="button"
            disabled={disabled}
            className={`
              relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full text-white shrink-0
              transition-all touch-manipulation active:scale-90
              ${disabled ? "bg-neutral-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"}
            `}
           onMouseDown={(e) => {
  // Keep textarea focused on desktop.
  if (!disabled) e.preventDefault();
}}
onClick={() => {
  if (disabled) return;

  if (editTarget) {
    onConfirmEdit?.();
  } else if (canSend) {
    handleSend();
  } else {
    startRecording?.();
  }
}}
aria-label={
  editTarget
    ? "Save edit"
    : canSend
      ? "Send message"
      : "Record voice message"
}
>
            {canSend ? <SendIcon /> : <MicIcon />}
          </button>
        </div>
      )}
    </div>
  );
}